import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import CloseIcon from '../../images/CloseIcon';
import { changeProductPrice } from '../../services/items.services';
import { showToast } from '../../services/toast.service';
import { useDialogFocus, useFormKeyboard } from '../../hook/useFormKeyboard';

interface ChangePriceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful save so the caller can refresh its data. */
  onSaved: () => void;
  productId: string | null;
  productName?: string;
  currentPrice: number | null;
  language: any;
}

/**
 * The simple price-change flow: current price, "Enter new price", and a
 * schedule toggle defaulting to Now (or a chosen future day). Saving calls
 * /api/inventory/product-price/change, which creates a NEW price record and
 * preserves history. Used from the Items list and the item editor.
 */
const ChangePriceDialog = ({
  isOpen,
  onClose,
  onSaved,
  productId,
  productName,
  currentPrice,
  language,
}: ChangePriceDialogProps) => {
  const [value, setValue] = useState<string>('');
  const [when, setWhen] = useState<'now' | 'date'>('now');
  const [date, setDate] = useState<Date | null>(null);
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  // Return in the price field saves on phones too.
  const kb = useFormKeyboard({ enterOnTouch: true });
  const dialogRef = useRef<HTMLDivElement>(null);

  // Fresh form every time the dialog opens.
  useEffect(() => {
    if (isOpen) {
      setValue('');
      setWhen('now');
      setDate(null);
      setError('');
      setSaving(false);
    }
  }, [isOpen]);

  const close = () => {
    if (!saving) onClose();
  };
  // Opens on the price input; on touch the hook focuses a button so no on-screen keyboard pops up.
  useDialogFocus(isOpen, dialogRef, close);

  const save = async () => {
    if (!productId || saving) return;
    const priceNum = Number(String(value).trim());
    if (String(value).trim() === '' || !Number.isFinite(priceNum) || priceNum < 0) {
      setError(language?.['Enter a valid price.'] ?? 'Enter a valid price.');
      return;
    }
    if (when === 'date' && !date) {
      setError(language?.['Choose a date.'] ?? 'Choose a date.');
      return;
    }
    setSaving(true);
    try {
      await changeProductPrice({
        productId,
        newPrice: priceNum,
        // Send the picked LOCAL calendar day (YYYY-MM-DD), not toISOString():
        // for a positive-UTC user (e.g. UTC+2) today's local midnight serialises
        // to the previous day in UTC, so the server rejects "today" as past.
        effectiveFrom:
          when === 'date' && date
            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            : null,
      });
      showToast({
        message: language?.['Price updated !'] ?? 'Price updated !',
        type: 'success',
        duration: 5000,
      });
      onClose();
      onSaved();
    } catch (err: any) {
      const serverMsg =
        err?.response?.data?.message && typeof err.response.data.message === 'string'
          ? err.response.data.message
          : null;
      setError(serverMsg ?? language?.['Price update failed.'] ?? 'Price update failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      } transition-opacity duration-300 ease-in-out`}
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={language?.['Change Price'] ?? 'Change Price'}
      style={{
        backgroundColor: '#22252552',
        zIndex: '200',
        // Out of the Tab order once faded out; visible at once on open so the input can take focus.
        transition: isOpen ? 'opacity 0.3s ease' : 'opacity 0.3s ease, visibility 0s linear 0.3s',
        visibility: isOpen ? 'visible' : 'hidden',
        width: '100vw',
        height: '100vh',
        left: 0,
        top: 0,
      }}
    >
      <div {...kb.scopeProps}>
      <div
        className={`transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-y-0' : 'translate-y-4 opacity-0'
        }`}
        style={{
          padding: '24px',
          borderRadius: '24px',
          backgroundColor: '#FFFFFF',
          width: '460px',
          transition: 'transform 0.3s ease, opacity 0.3s ease',
        }}
      >
        <div className="flex items-start justify-between">
          <div>
            <p
              style={{ fontSize: '20px', color: '#222525' }}
              className="font-semibold font-inter leading-[24px]"
            >
              {language && language?.['Change Price'] ? language?.['Change Price'] : 'Change Price'}
            </p>
            <p
              style={{ fontSize: '14px', color: '#6A7878', marginTop: '8px' }}
              className="font-inter leading-[20px]"
            >
              {productName}
            </p>
          </div>
          <button
            type="button"
            aria-label={language?.['Close'] ?? 'Close'}
            onClick={close}
            className="flex items-center justify-center cursor-pointer"
            style={{ width: '24px', height: '24px' }}
          >
            <CloseIcon />
          </button>
        </div>
        <div style={{ marginTop: '16px' }}>
          <div
            className="flex items-center justify-between"
            style={{ padding: '12px 16px', backgroundColor: '#EBF0F0', borderRadius: '12px' }}
          >
            <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#4A5353' }}>
              {language && language?.['Current price'] ? language?.['Current price'] : 'Current price'}
            </p>
            <p
              className="text-[16px] font-inter leading-[24px] font-semibold"
              style={{ color: '#222525' }}
            >
              {currentPrice != null ? currentPrice.toFixed(2) : '—'}
            </p>
          </div>
          <div className="space-y-2" style={{ marginTop: '20px' }}>
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: '#4A5353' }}
              >
                {language && language?.['Enter new price'] ? language?.['Enter new price'] : 'Enter new price'}
              </p>
              <p
                className="text-[14px] font-inter leading-[20px] font-medium"
                style={{ color: '#BA0E13' }}
              >
                {language && language?.['*'] ? language?.['*'] : '*'}
              </p>
            </div>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={value}
              onChange={(e: any) => {
                setValue(e.target.value);
                setError('');
              }}
              className="text-[16px] font-inter placeholder:text-placeholder text-main border border-[#DBE4E4] px-3 py-3 rounded-xl outline-none focus-none bg-transparent w-full"
            />
          </div>
          <div className="space-y-2" style={{ marginTop: '20px' }}>
            <p
              className="text-[14px] font-inter leading-[20px] font-medium"
              style={{ color: '#4A5353' }}
            >
              {language && language?.['Schedule price change']
                ? language?.['Schedule price change']
                : 'Schedule price change'}
            </p>
            <div className="flex items-center" style={{ gap: '8px' }}>
              <button
                type="button"
                aria-pressed={when === 'now'}
                onClick={() => {
                  setWhen('now');
                  setError('');
                }}
                className="cursor-pointer text-[14px] font-inter font-medium"
                style={{
                  padding: '8px 20px',
                  borderRadius: '30px',
                  border: `1px solid ${when === 'now' ? '#0F7679' : '#DBE4E4'}`,
                  backgroundColor: when === 'now' ? '#0F7679' : 'transparent',
                  color: when === 'now' ? '#FFFFFF' : '#4A5353',
                }}
              >
                {language && language?.['Now'] ? language?.['Now'] : 'Now'}
              </button>
              <button
                type="button"
                aria-pressed={when === 'date'}
                onClick={() => {
                  setWhen('date');
                  setError('');
                }}
                className="cursor-pointer text-[14px] font-inter font-medium"
                style={{
                  padding: '8px 20px',
                  borderRadius: '30px',
                  border: `1px solid ${when === 'date' ? '#0F7679' : '#DBE4E4'}`,
                  backgroundColor: when === 'date' ? '#0F7679' : 'transparent',
                  color: when === 'date' ? '#FFFFFF' : '#4A5353',
                }}
              >
                {language && language?.['On a date'] ? language?.['On a date'] : 'On a date'}
              </button>
            </div>
            {when === 'date' && (
              <div className="w-full" style={{ marginTop: '8px' }}>
                <DatePicker
                  dateFormat="dd-MM-yyyy"
                  minDate={new Date()}
                  selected={date}
                  placeholderText={
                    language && language?.['Choose a date'] ? language?.['Choose a date'] : 'Choose a date'
                  }
                  onChange={(d: any) => {
                    setDate(d);
                    setError('');
                  }}
                  className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#DBE4E4] p-3 rounded-xl outline-none focus-none bg-transparent w-full"
                />
              </div>
            )}
          </div>
          {error && (
            <p className="error" style={{ marginTop: '12px' }}>
              {error}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
          <button
            type="button"
            onClick={close}
            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
            style={{
              borderRadius: '40px',
              padding: '10px 24px',
              border: '1px solid #C9D7D8',
              color: '#222525',
              opacity: saving ? 0.5 : 1,
              pointerEvents: saving ? 'none' : 'auto',
            }}
          >
            {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
          </button>
          <button
            type="button"
            {...kb.primaryProps(save)}
            className="text-[14px] leading-[22px] font-inter font-medium"
            style={{
              borderRadius: '40px',
              padding: '10px 24px',
              backgroundColor: saving ? '#9CA3AF' : '#0F7679',
              color: '#FFFFFF',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving
              ? (language?.['Saving...'] ?? 'Saving...')
              : (language?.['Save Price'] ?? 'Save Price')}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ChangePriceDialog;
